import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  type SkinId,
  applySkinToRoot,
  ensureSkinFonts,
  getSkin,
  persistSkin,
  readInitialSkin,
} from "./skins";

/**
 * Available theme modes for user settings
 * @typedef {'light' | 'dark' | 'system'} ThemeMode
 */
export type ThemeMode = "light" | "dark" | "system";

/**
 * Theme settings object for local storage
 * @interface ThemeSettings
 */
interface ThemeSettings {
  /** Current theme mode choice ('light', 'dark', or 'system') */
  mode: ThemeMode;
  /** Last explicitly selected theme (used when switching from 'system' to explicit choice) */
  lastExplicitChoice?: "light" | "dark";
  /** Whether transitions should be enabled (disabled during initial load) */
  enableTransitions: boolean;
}

/**
 * Core theme context type that provides the primary API
 * @interface ThemeContextType
 */
interface ThemeContextType {
  /** Current dark mode state (true for dark mode, false for light mode) */
  darkMode: boolean;
  /** Explicit function to set dark mode state directly */
  setDarkMode: (value: boolean) => void;
  /** Toggle function to switch between dark and light modes */
  toggleDarkMode: () => void;
  /** Current theme mode setting ('light', 'dark', or 'system') */
  themeMode: ThemeMode;
  /** Function to set the theme mode explicitly */
  setThemeMode: (mode: ThemeMode) => void;
  /** Whether system dark mode preference is enabled */
  isSystemDarkMode: boolean;
  /** Whether theme transitions are currently enabled */
  transitionsEnabled: boolean;
  /** Resets theme to system preference */
  resetToSystemPreference: () => void;
  /** Enables/disables theme transitions (for testing purposes) */
  setTransitionsEnabled: (enabled: boolean) => void;
  /** Applies a temporary theme transition class that automatically removes itself */
  applyTransition: () => void;

  /** Active interface theme ("skin"), independent of light/dark. */
  skin: SkinId;
  /** Switch the interface theme; persisted per browser. */
  setSkin: (skin: SkinId) => void;
  /** True when the active skin is dark-only: darkMode is forced on and the
   * light/dark toggle does nothing. The saved light/dark choice is kept and
   * comes back when the user switches to a skin that has a light mode. */
  skinForcesDark: boolean;

  // Legacy API (for backwards compatibility) - will be removed in future versions
  /** @deprecated Use darkMode instead */
  isDarkMode: boolean;
  /** @deprecated Use toggleDarkMode instead */
  toggleTheme: () => void;
}

// Default theme settings
// The Kinetic Command Center palette is dark-only (see DESIGN.md). Following
// a light OS preference left `dark:` text variants off on dark surfaces.
const DEFAULT_THEME_SETTINGS: ThemeSettings = {
  mode: "dark",
  enableTransitions: true,
};

// Constants for managing theme
const STORAGE_KEY = "nexusDialerTheme";
const THEME_TRANSITION_CLASS = "theme-transition";
const THEME_SWITCHING_CLASS = "theme-switching";
const THEME_LOADED_CLASS = "theme-loaded";
const TRANSITION_DURATION_MS = 200; // Reduced for snappier transitions
const DARK_CLASS = "dark";

// Signal tokens key off data-theme; Tailwind `dark:` variants key off the class.
const applyThemeToRoot = (root: HTMLElement, isDark: boolean) => {
  root.classList.toggle(DARK_CLASS, isDark);
  root.setAttribute("data-theme", isDark ? "dark" : "light");
};

/**
 * Creates a context with undefined default value
 * Will ensure proper error handling if used outside provider
 */
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/**
 * Props for the ThemeProvider component
 * @interface ThemeProviderProps
 */
interface ThemeProviderProps {
  /** Child components that will have access to theme context */
  children: ReactNode;
  /** Optional initial theme mode to apply (defaults to 'system') */
  initialThemeMode?: ThemeMode;
  /** Optional function for testing theme changes */
  onThemeChange?: (isDark: boolean) => void;
}

/**
 * Theme Provider component that manages dark/light mode state
 *
 * This provider is responsible for the entire theme management lifecycle including:
 * 1. Managing the application theme state (darkMode)
 * 2. Persisting user theme preferences in localStorage
 * 3. Synchronizing with OS-level theme preferences when appropriate
 * 4. Applying the theme to the document via the 'dark' class on the root element
 * 5. Providing theme values and toggle functions to consuming components
 * 6. Managing smooth theme transitions between states
 *
 * Theme Preference Hierarchy:
 * 1. User preference from localStorage (highest priority)
 * 2. OS-level preference via prefers-color-scheme media query
 * 3. Default to light mode if neither is set
 *
 * Performance Optimizations:
 * - Prevents transition animations on initial page load
 * - Uses cleanup functions to prevent memory leaks
 * - Handles browser compatibility with fallbacks for older browsers
 * - Prevents unnecessary re-renders with useMemo and useCallback
 *
 * Backward Compatibility:
 * - Maintains legacy API (isDarkMode, toggleTheme) for older components
 * - Will remove deprecated API in future versions
 *
 * @example
 * // Wrap your application with the ThemeProvider
 * import { ThemeProvider } from './contexts/ThemeContext';
 *
 * const App = () => (
 *   <ThemeProvider>
 *     <YourApp />
 *   </ThemeProvider>
 * );
 *
 * @example
 * // Use the theme context in a component
 * import { useTheme } from './contexts/ThemeContext';
 *
 * const ThemedComponent = () => {
 *   const { darkMode, toggleDarkMode } = useTheme();
 *   return (
 *     <div>
 *       <p>Current theme: {darkMode ? 'Dark' : 'Light'}</p>
 *       <button onClick={toggleDarkMode}>Toggle Theme</button>
 *     </div>
 *   );
 * };
 *
 * @see dark-mode-consolidated.css - Contains dark mode CSS variables
 * @see themeUtils.js - Provides helper functions for theme-aware styling
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  initialThemeMode = "system",
  onThemeChange,
}) => {
  // Get system dark mode preference using media query
  const getSystemDarkModePreference = (): boolean => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  };

  // Load theme settings from localStorage or use defaults
  const loadThemeSettings = (): ThemeSettings => {
    if (typeof window === "undefined") return DEFAULT_THEME_SETTINGS;

    try {
      const savedSettings = localStorage.getItem(STORAGE_KEY);
      if (savedSettings) {
        return JSON.parse(savedSettings) as ThemeSettings;
      }

      // Legacy support for old localStorage format
      const savedTheme = localStorage.getItem("theme");
      const savedThemeMode = localStorage.getItem("themeMode");

      if (savedThemeMode || savedTheme) {
        // Convert legacy format to new format
        const legacyMode = (savedThemeMode as ThemeMode) || "system";
        const legacyDarkMode = savedTheme === "dark";

        const migratedSettings: ThemeSettings = {
          mode: legacyMode,
          enableTransitions: true,
        };

        // If explicit mode was selected, store as lastExplicitChoice
        if (legacyMode === "light" || legacyMode === "dark") {
          migratedSettings.lastExplicitChoice = legacyMode;
        } else if (legacyDarkMode) {
          // If system mode but dark theme was active, store dark as last choice
          migratedSettings.lastExplicitChoice = "dark";
        }

        return migratedSettings;
      }
    } catch (error) {}

    return DEFAULT_THEME_SETTINGS;
  };

  // State for system preference tracking
  const [isSystemDarkMode, setIsSystemDarkMode] = useState<boolean>(
    getSystemDarkModePreference,
  );

  // Load initial theme settings
  const initialSettings = loadThemeSettings();
  initialSettings.mode = initialSettings.mode || initialThemeMode;

  // State for theme settings
  const [themeSettings, setThemeSettings] =
    useState<ThemeSettings>(initialSettings);

  // Compute actual dark mode state based on theme mode and system preference
  const [darkMode, setDarkMode] = useState<boolean>(() => {
    if (themeSettings.mode === "dark") return true;
    if (themeSettings.mode === "light") return false;
    return getSystemDarkModePreference();
  });

  // Interface theme. Dark-only skins override the light/dark preference
  // without overwriting it.
  const [skin, setSkinState] = useState<SkinId>(readInitialSkin);
  const skinForcesDark = getSkin(skin).darkOnly;
  const effectiveDark = skinForcesDark || darkMode;

  useEffect(() => {
    if (typeof document === "undefined") return;
    applySkinToRoot(document.documentElement, skin);
    ensureSkinFonts(skin);
  }, [skin]);

  const setSkin = useCallback((next: SkinId): void => {
    persistSkin(next);
    setSkinState(next);
  }, []);

  // Mark theme as loaded after initial render
  useEffect(() => {
    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      document.documentElement.classList.add(THEME_LOADED_CLASS);
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  // State for tracking transition enabling
  const [transitionsEnabled, setTransitionsEnabled] = useState<boolean>(
    themeSettings.enableTransitions,
  );

  const transitionTimer = useRef<number | undefined>(undefined);
  const beginTransition = useCallback(() => {
    const root = document.documentElement;
    clearTimeout(transitionTimer.current);
    root.classList.add(THEME_TRANSITION_CLASS);
    transitionTimer.current = window.setTimeout(() => {
      transitionTimer.current = undefined;
      root.classList.remove(THEME_TRANSITION_CLASS);
    }, TRANSITION_DURATION_MS);
  }, []);

  // Save theme settings to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(themeSettings));

      // Legacy support - maintain old localStorage format for backward compatibility
      localStorage.setItem("themeMode", themeSettings.mode);
      localStorage.setItem("theme", darkMode ? "dark" : "light");
    } catch {
      // Storage blocked: the choice lasts this page only.
    }
  }, [themeSettings, darkMode]);

  // Apply dark mode class to document
  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    let firstFrame: number | null = null;
    let secondFrame: number | null = null;

    // Skip transitions on initial mount
    const isInitialMount =
      !document.documentElement.classList.contains(THEME_LOADED_CLASS);

    if (!isInitialMount && transitionsEnabled) {
      // Add switching class to disable all transitions temporarily
      document.documentElement.classList.add(THEME_SWITCHING_CLASS);

      // Use RAF to ensure the switching class is applied before theme change
      firstFrame = requestAnimationFrame(() => {
        firstFrame = null;
        // Apply or remove dark class
        applyThemeToRoot(document.documentElement, effectiveDark);

        // Remove switching class and add transition class
        secondFrame = requestAnimationFrame(() => {
          secondFrame = null;
          root.classList.remove(THEME_SWITCHING_CLASS);
          beginTransition();
        });
      });
    } else {
      // No transitions - apply immediately
      applyThemeToRoot(document.documentElement, effectiveDark);
    }

    // Notify any test listeners
    if (onThemeChange) {
      onThemeChange(effectiveDark);
    }
    return () => {
      if (firstFrame !== null) cancelAnimationFrame(firstFrame);
      if (secondFrame !== null) cancelAnimationFrame(secondFrame);
      clearTimeout(transitionTimer.current);
      transitionTimer.current = undefined;
      root.classList.remove(THEME_SWITCHING_CLASS, THEME_TRANSITION_CLASS);
    };
  }, [effectiveDark, transitionsEnabled, onThemeChange, beginTransition]);

  // Update dark mode when theme mode changes
  useEffect(() => {
    if (themeSettings.mode === "system") {
      setDarkMode(isSystemDarkMode);
    } else {
      setDarkMode(themeSettings.mode === "dark");
    }
  }, [themeSettings.mode, isSystemDarkMode]);

  // Listen for system theme preference changes
  useEffect(() => {
    const prefersDarkMedia = window.matchMedia("(prefers-color-scheme: dark)");

    const handleSystemThemeChange = (e: MediaQueryListEvent): void => {
      setIsSystemDarkMode(e.matches);

      // Only update active theme if in system mode
      if (themeSettings.mode === "system") {
        setDarkMode(e.matches);
      }
    };

    // Use the correct event listener based on browser support
    try {
      // Modern browsers
      prefersDarkMedia.addEventListener("change", handleSystemThemeChange);
      return () =>
        prefersDarkMedia.removeEventListener("change", handleSystemThemeChange);
    } catch (e) {
      // Fallback for older browsers (Safari < 14, IE, etc.)
      prefersDarkMedia.addListener(handleSystemThemeChange as any);
      return () =>
        prefersDarkMedia.removeListener(handleSystemThemeChange as any);
    }
  }, [themeSettings.mode]);

  /**
   * Explicitly set the theme mode with type safety
   * Will update the theme settings and persist to localStorage
   */
  const setThemeMode = useCallback((mode: ThemeMode): void => {
    setThemeSettings((prev) => {
      const newSettings = { ...prev, mode };

      // Store explicit user choice for future reference
      if (mode === "light" || mode === "dark") {
        newSettings.lastExplicitChoice = mode;
      }

      return newSettings;
    });
  }, []);

  /**
   * Toggle between dark and light modes
   * If in system mode, switches to explicit light/dark mode based on current state
   */
  const toggleDarkMode = useCallback((): void => {
    if (skinForcesDark) return;
    setThemeSettings((prev) => {
      const newDarkMode = !darkMode;

      // If we're in system mode, switch to explicit mode matching the toggled state
      if (prev.mode === "system") {
        return {
          ...prev,
          mode: newDarkMode ? "dark" : "light",
          lastExplicitChoice: newDarkMode ? "dark" : "light",
        };
      }

      // Otherwise just toggle between light and dark
      return {
        ...prev,
        mode: prev.mode === "dark" ? "light" : "dark",
        lastExplicitChoice: prev.mode === "dark" ? "light" : "dark",
      };
    });
  }, [darkMode, skinForcesDark]);

  /**
   * Reset the theme to follow system preference
   */
  const resetToSystemPreference = useCallback((): void => {
    setThemeSettings((prev) => ({
      ...prev,
      mode: "system",
    }));
  }, []);

  /**
   * Apply a temporary transition class that will auto-remove itself
   * Useful for components that need to trigger transitions manually
   */
  const applyTransition = useCallback((): void => {
    if (!transitionsEnabled) return;

    beginTransition();
  }, [transitionsEnabled, beginTransition]);

  /**
   * Explicitly set dark mode state (for controlled components)
   * Will update theme mode to match the requested state
   */
  const setDarkModeExplicit = useCallback((value: boolean): void => {
    setThemeSettings((prev) => ({
      ...prev,
      mode: value ? "dark" : "light",
      lastExplicitChoice: value ? "dark" : "light",
    }));
  }, []);

  /**
   * Enable or disable theme transitions
   * Useful for testing and for preventing transitions on initial load
   */
  const setTransitionsEnabledExplicit = useCallback(
    (enabled: boolean): void => {
      setTransitionsEnabled(enabled);
      setThemeSettings((prev) => ({
        ...prev,
        enableTransitions: enabled,
      }));
    },
    [],
  );

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    (): ThemeContextType => ({
      // Core theme state
      darkMode: effectiveDark,
      setDarkMode: setDarkModeExplicit,
      toggleDarkMode,

      // Theme mode management
      themeMode: themeSettings.mode,
      setThemeMode,

      // System preference tracking
      isSystemDarkMode,

      // Transition management
      transitionsEnabled,
      setTransitionsEnabled: setTransitionsEnabledExplicit,
      applyTransition,

      // Helper functions
      resetToSystemPreference,

      // Interface theme
      skin,
      setSkin,
      skinForcesDark,

      // Legacy API for backwards compatibility
      isDarkMode: effectiveDark,
      toggleTheme: toggleDarkMode,
    }),
    [
      effectiveDark,
      skin,
      setSkin,
      skinForcesDark,
      setDarkModeExplicit,
      toggleDarkMode,
      themeSettings.mode,
      setThemeMode,
      isSystemDarkMode,
      transitionsEnabled,
      setTransitionsEnabledExplicit,
      applyTransition,
      resetToSystemPreference,
    ],
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Custom hook for accessing the theme context in components
 *
 * This hook provides type-safe access to the current theme state and theme control functions.
 * It includes validation to ensure it's only used within a ThemeProvider component.
 *
 * @returns {ThemeContextType} Theme context object with the following properties:
 * - `darkMode`: Current dark mode state (true for dark mode, false for light mode)
 * - `setDarkMode`: Function to directly set dark mode state
 * - `toggleDarkMode`: Function to toggle between dark and light modes
 * - `themeMode`: Current theme mode ('light', 'dark', or 'system')
 * - `setThemeMode`: Function to explicitly set the theme mode
 * - `isSystemDarkMode`: Whether the system preference is currently dark mode
 * - `transitionsEnabled`: Whether theme transitions are enabled
 * - `setTransitionsEnabled`: Function to enable/disable transitions
 * - `applyTransition`: Function to trigger a temporary transition
 * - `resetToSystemPreference`: Function to reset theme to system preference
 * - `isDarkMode`: Legacy alias for darkMode (deprecated)
 * - `toggleTheme`: Legacy alias for toggleDarkMode (deprecated)
 *
 * @throws {Error} If used outside of a ThemeProvider component
 *
 * @example
 * import { useTheme } from '../../contexts/ThemeContext';
 * import { getThemeStyles } from '../../utils/themeUtils';
 *
 * const ThemedComponent = () => {
 *   const { darkMode, toggleDarkMode } = useTheme();
 *
 *   const cardStyles = getThemeStyles({
 *     darkMode,
 *     light: { backgroundColor: '#ffffff', color: '#333333' },
 *     dark: { backgroundColor: '#1f2937', color: '#f9fafb' }
 *   });
 *
 *   return (
 *     <div style={cardStyles}>
 *       <p>Current theme: {darkMode ? 'Dark' : 'Light'}</p>
 *       <button onClick={toggleDarkMode}>Toggle Theme</button>
 *     </div>
 *   );
 * };
 *
 * @see ThemeProvider - Provider component that must wrap components using this hook
 * @see themeUtils - Utility functions for theme-aware styling
 */
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error(
      "useTheme must be used within a ThemeProvider. " +
        "Ensure your component is wrapped in a ThemeProvider component.",
    );
  }

  return context;
};

/**
 * Export the raw context for advanced use cases
 * Most components should use the useTheme hook instead
 *
 * @deprecated Use useTheme hook instead for type safety and validation
 */
export default ThemeContext;
